import { Component, Output, EventEmitter, Input, ViewChild, ElementRef, AfterViewInit } from "@angular/core";

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface BPInputModel {
	[key: string]: any;

	hasError: (field: string) => boolean;
	clearError: () => void;
	wiggle: (field: string) => string;
}

/**
 * your trusty text-field for everything!
 * Example of usage:
 * <example-url>/components/bp-input/bp-input.demo.component.html</example-url>
 */
@Component({
	selector: "bp-input",
	templateUrl: "./bp-input.component.html",
	styleUrls: ["./bp-input.component.scss"],
	standalone: false
})
export class BpInputComponent implements AfterViewInit {
	public get value(): any {
		if (!this.model || !this.field) {
			return "";
		}
		return this.model[this.field];
	}

	public set value(newVal: any) {
		this.model[this.field] = newVal;
		this.modelChange.emit(this.model);
	}

	@Input("value")
	public get _value(): string {
		if (this.field) {
			return this.value;
		}
		return this.stringValue || "";
	}

	public set _value(val) {
		if (this.secondLine.length > 0) {
			// Combine the values of the specified fields into a single string
			this.secondLineConcat = this.secondLine
				.filter((fieldName) => !!fieldName)
				.map((fieldName) => this.model[fieldName])
				.join(" - ");
		}
		if (BpInputComponent.equal(this.stringValue, val) && !!this.stringValue) {
			return;
		}

		if (this.field) {
			this.value = val;
			this.inputGroup.nativeElement.value = val;
		} else {
			this.stringValue = val;
		}
		this.valueChange.emit(val);
		this.modelChange.emit(this.model);
		this.clearErrorInput();

		if (this.maxChars && val.length === this.maxChars + 1) {
			if (!this.errorBoolean) {
				this.model.wiggle(this.field);
			}
			this.errorBoolean = true;
		}
		if (this.errorBoolean && val.length <= this.maxChars) {
			this.model.clearError();
			this.errorBoolean = false;
		}
	}

	public get wiggle(): boolean {
		if (!this.model || !this.field || !this.model.hasOwnProperty(this.field + "_wiggle")) {
			return false;
		}

		return this.model[this.field + "_wiggle"];
	}

	@ViewChild("inputGroup")
	public inputGroup!: ElementRef;

	@Input() public inputError = false;

	@Input()
	public precision = 0;

	/**
	 * apply the error style - this will be automatically applied if there are any errors on the 'field' validate able
	 */
	@Input()
	public error = "";
	@Input()
	public validationError = "";
	/**
	 * the optional description text shown on top of the input
	 */
	@Input()
	public label = "";

	/**
	 * the disabled input field in the verification flow needs a special style
	 */
	@Input() public verificationFlowFlag: boolean = false;
	@Input() public isBpPhone: boolean = false;

	/**
	 * (i) Tooltip which appears in the right part of the label
	 * Url is used for <a href>
	 * Description shows the mouse hover message
	 */
	@Input()
	public infoTooltipUrl = "";

	/**
	 * should the class 'form-group' be present on the root element
	 */
	@Input()
	public formGroupClass = true;

	@Input()
	public infoTooltipDescription = "";

	@Input()
	public type = "";
	/**
	 * the placeholder text displayed inside the input
	 */
	@Input()
	public placeholder = "";

	/**
	 * specifies the maximum number of characters allowed in the <input> element.
	 */
	@Input()
	public maxlength: number | null = null;

	/**
	 * Alternative option for maxlength: allows for input above maxlength, but colors field red
	 */
	@Input()
	public maxChars = 0;

	/**
	 * enables clear button functionality and can take a helpdesk string
	 */
	@Input()
	public hcArticle = "";

	@Input()
	public hcArticleTooltipTranslation = "";
	/**
	 * set the input readonly
	 */
	@Input()
	public readonly = false;
	/**
	 * set the input disabled
	 */
	@Input()
	public disabled = false;
	/**
	 * set the inputs background transparent
	 */
	@Input()
	public transparent = false;

	/**
	 * set the input flag for enabling/disabling the char counter
	 */
	@Input()
	public showCharCounter = true;

	/**
	 * set if the icon should be in front of the input otherwise it will be at the end
	 */
	@Input()
	public iconLeading = false;
	/**
	 * set the icon-font icon to be displayed next to the input
	 */
	@Input()
	public icon = "";

	/**
	 * set the image from a link source
	 */
	@Input()
	public iconUrl = "";

	@Input()
	public iconUrlAltText = "";

	/**
	 * needs article string from the correct helpdesk article, otherwise it will link to the helpdesk start page
	 */

	@Output()
	public inputChange: EventEmitter<any> = new EventEmitter<any>();

	// tslint:disable-next-line:no-output-on-prefix
	@Output()
	public onFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
	// tslint:disable-next-line:no-output-on-prefix
	@Output()
	public onFocusOut: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
	// tslint:disable-next-line:no-output-on-prefix
	@Output()
	public onKeyEnter: EventEmitter<any> = new EventEmitter<any>();

	@Output()
	public valueChange: EventEmitter<any> = new EventEmitter<any>();

	@Output()
	public inputGroupEvent: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

	@Output()
	public onInputModified: EventEmitter<string> = new EventEmitter<string>();
	/**
	 * the Validate able Object holding the field you want to bind to this input
	 */
	@Input()
	public model: BPInputModel = { clearError: () => undefined, hasError: () => false, wiggle: () => "" };
	@Output()
	public modelChange = new EventEmitter<any>();

	public stringValue = "";

	private errorBoolean = false;

	/**
	 * the regex pattern to apply to the value
	 */
	@Input()
	public pattern = "";

	/**
	 * the name of the field on the model object you want to bind this input to
	 */
	@Input()
	public field = "";

	/**
	 * second Line in the Input field
	 */
	@Input()
	public secondLine: string[] = [];

	/**
	 * set the step size of the input
	 */
	@Input()
	public step = 0;

	/**
	 * minimum value (for supporting input types eg. number)
	 */
	@Input()
	public min = "";

	/**
	 * maximum value (for supporting input types eg. number)
	 */
	@Input()
	public max = "";

	/**
	 * display text at the end of the input
	 */
	@Input()
	public textInside = "";

	// tslint:disable-next-line:no-input-rename
	@Input("phone-region")
	public phoneRegion = "";
	// tslint:disable-next-line:no-input-rename
	@Input("formatted-output")
	public formattedOutput = false;
	@Input()
	public focus = false;

	@Input()
	public languageIsoCode = "en";

	@Input()
	public hasSecondLine: boolean = false;

	@Input()
	public displayLogo: boolean = false;

	private static equal(object1: any, object2: any): boolean {
		return JSON.stringify(object1) === JSON.stringify(object2) && typeof object1 === typeof object2;
	}

	public secondLineConcat: string = "";
	public isActive: boolean = false;
	private firstChange: boolean = true;

	public ngAfterViewInit(): void {
		this.inputGroupEvent.emit(this.inputGroup);
	}

	public clearButton(): void {
		if (this._value.length > 0) {
			this._value = "";
			this.inputGroup.nativeElement.value = "";
			this.clearErrorInput();
		}
	}

	public clearErrorInput(): void {
		if (this.model.hasError(this.field)) {
			this.model.clearError();
		}
		if (this.error) {
			this.model.clearError();
		}
		this.modelChange.emit(this.model);
	}

	public hasError(): boolean {
		return !!this.error || (this.model && this.model.hasError(this.field));
	}

	public showMaxLength(length: number | null): boolean {
		return !!length && this.showCharCounter;
	}

	public getHelpdeskLink(): string {
		if (this.hcArticle.length > 0) {
			return this.hcArticle;
		}
		return "https://support.bitpanda.com/hc/" + (this.languageIsoCode === "en" ? "en-us" : this.languageIsoCode);
	}

	public hasValue(): boolean {
		return !!this.stringValue || !!this.value;
	}

	public onInputFocus(e: FocusEvent): void {
		this.onFocus.emit(e);
		this.isActive = true;
	}

	public onInputFocusOut(e: FocusEvent): void {
		this.onFocusOut.emit(e);
		this.isActive = false;
	}

	public onKeydownEnter(): void {
		this.onKeyEnter.emit();
		this.isActive = true;
	}

	public inputModified(input: string): void {
		if (this.firstChange) {
			this.firstChange = false;
			return;
		}
		this.onInputModified.emit(input);
	}
}
